import type { DeepReadonly, Ref } from 'vue'
import type { PerspectiveCamera, Scene } from 'three'
import type { Drill } from '~/entities/drill'
import type {
  DrillDataLocalization,
  DrillDataSet,
  DrillDataSetAggregate,
  DrillLabelApiData,
  DrillLayer,
  DrillCSVFile,
} from '~/entities/drillLabel'
import type { DrillLegendViewModel } from '~/entities/drillHole'
import type { ModelView } from '~/entities/meeting'
import type { DrillLabelVisibility } from '~/entities/model3d'
import { DrillLabelManager } from '~/lib/three-d-resources/drill-label-manager'
import { disposeScene } from '~/lib/three-d-viewer/utils'
import { DEFAULT_FONT_SIZE } from '../constants/labels'
import { DrillHoleManager } from './drill-hole-manager'

export class DrillDataSetManager {
  public drillLabelManager: DrillLabelManager
  public drillHoleManager: DrillHoleManager
  public drillDataSets: DrillDataSet[] = []
  private modelId: number | null = null
  private sceneId: number | null = null
  private companyId: number | null = null
  private mode: 'view' | 'edit' = 'view'

  constructor(
    private loaderConfig: { uuid: string | null },
    private onLabelsChanged: () => void,
    onVisibilityChanged?: (payload: DrillLabelVisibility[]) => void,
    onToggleCollapsed?: (payload: { id: number; is_collapsed: boolean }) => void,
    currentView?: Ref<DeepReadonly<ModelView>>,
    maxAge: number = 0,
  ) {
    this.drillLabelManager = new DrillLabelManager(
      this.mode,
      loaderConfig,
      onLabelsChanged,
      onVisibilityChanged,
      onToggleCollapsed,
      currentView,
      maxAge,
    )
    this.drillHoleManager = new DrillHoleManager(loaderConfig, onLabelsChanged)
  }

  async loadDrillDataSet({ companyId, modelId }: { companyId: number; modelId: number }) {
    let dataSets: DrillDataSet[]

    try {
      this.modelId = modelId
      this.companyId = companyId

      dataSets = await this.getDataSets({ companyId, modelId })
    } catch {
      dataSets = []
    }

    if (this.modelId !== modelId) return

    this.drillDataSets = dataSets
    await this.fetchAllDataSets()
  }

  async getDataSets({ companyId, modelId }: { companyId: number; modelId: number }) {
    return await $fetch<DrillDataSet[]>(`/v2/drill/companies/${companyId}/mappers`, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
      params: {
        sort_by: '-created_at',
        include: ['data_set', 'localization'],
        model_id: modelId,
      },
    })
  }

  async updateDataSets({
    companyId,
    drillDataSets,
    drillDataSetUpdates,
  }: {
    companyId: number
    drillDataSets: DrillDataSet[]
    drillDataSetUpdates: Partial<Drill>
  }) {
    await Promise.all(
      drillDataSets.map(drill =>
        $fetch(`/v2/drill/companies/${companyId}/data/${drill.data_set_id}`, {
          ...useFetchSharedOptions(),
          method: 'PATCH',
          body: drillDataSetUpdates,
        }),
      ),
    )
  }

  async addDataSet({ drillDataSet, localization }: { drillDataSet: Drill; localization: DrillDataLocalization }) {
    if (!this.modelId) return

    const newDataSet = {
      data_set_id: drillDataSet.id,
      data_set: drillDataSet,
      localization_id: localization.id,
      localization,
      model_id: this.modelId,
    }

    this.drillDataSets = [newDataSet].concat(this.drillDataSets.filter(({ data_set_id }) => data_set_id !== newDataSet.data_set_id))

    await this.drillLabelManager.addLabelsForDataSet({ drillDataSet: newDataSet })
    await this.drillHoleManager.addHolesForDataSet(this.drillDataSets)

    this.onLabelsChanged()
  }

  async removeDataSet(dataSetId: number) {
    this.drillDataSets = this.drillDataSets.filter(dataSet => dataSet.data_set_id !== dataSetId)

    this.drillLabelManager.removeLabelsForDataSet(dataSetId)
    this.drillHoleManager.removeLegendForDataSet(dataSetId)
    this.drillHoleManager.removeDrillHolesForDataSet(dataSetId)
    this.onLabelsChanged()
  }

  private async fetchAllDataSets() {
    if (!this.modelId) return
    await this.drillLabelManager.fetchLabels({ modelId: this.modelId, drillDataSets: this.drillDataSets })
    await this.drillHoleManager.fetchHoles({ modelId: this.modelId, drillDataSets: this.drillDataSets })
  }

  setSceneId(sceneId: number | null) {
    this.sceneId = sceneId
    this.drillLabelManager.setSceneId(sceneId)
    this.drillHoleManager.setLegendsForScene(sceneId)
  }

  setSceneIdToCurrent() {
    return this.drillLabelManager.setSceneId(this.sceneId)
  }

  getDrillDataSetAggregate(drillDataSetId: number): DrillDataSetAggregate {
    const dataSet = this.drillDataSets.find(({ data_set_id }) => data_set_id === drillDataSetId)

    if (!dataSet) throw new Error(`Data set ${drillDataSetId} not found`)

    const drillApiData = this.drillHoleManager.getHolesForDrillDataSet(drillDataSetId)
    const layersWithFiles = this.drillLabelManager.getLabelsForDrillDataSet(drillDataSetId)
    const layersWithoutFiles = (drillApiData?.data || []).map(
      hole =>
        ({
          id: hole.hole_id,
          type: 'drill',
          title: hole.hole_id,
          visible: true,
          files: [] as DrillCSVFile[],
        }) as DrillLayer,
    )
    const drillLayers = layersWithFiles.length ? this._getDrillLabelLayers(layersWithFiles) : layersWithoutFiles
    const drillLegends = this._getDrillLayerLegends(this.drillHoleManager.getLegendList().filter(x => x.dataSetId === drillDataSetId))

    return { ...dataSet, drillLayers: [...drillLegends, ...drillLayers] }
  }

  private _getDrillLayerLegends(legends: DrillLegendViewModel[]): DrillLayer[] {
    return legends
      .map(({ id, name }) => {
        return {
          id,
          type: 'legend',
          title: name,
          visible: this.drillHoleManager.isLegendVisible(id),
          files: [] as DrillCSVFile[],
        }
      })
      .flat(Infinity) as DrillLayer[]
  }

  private _getDrillLabelLayers(labels: DrillLabelApiData[]): DrillLayer[] {
    const holes = this._groupDataBy('hole_id', labels)

    return Object.entries(holes).map(([title, labels]) => {
      const files = this._groupDataBy('file_id', labels as any)
      const groupedFiles = Object.entries(files).map(([fileId, labels]) => {
        const drillLabels = labels.map(label => ({
          id: label.id,
          title: this._getDrillLabelName(label),
          holeId: label.hole_id,
          visible: this.drillLabelManager.isVisible(label.id),
        }))

        return {
          fileId,
          title: labels[0].file!.name,
          visible: drillLabels.some(l => l.visible),
          labels: drillLabels,
        }
      })

      return {
        id: title,
        type: 'drill',
        title,
        visible: groupedFiles.some(f => f.visible),
        files: groupedFiles,
      }
    })
  }

  private _groupDataBy(groupingId: 'hole_id' | 'file_id', labels: DrillLabelApiData[]): { [key: string]: DrillLabelApiData[] } {
    return labels.reduce((acc: { [key: string]: DrillLabelApiData[] }, item: DrillLabelApiData) => {
      const key = item[groupingId] as string

      if (!acc[key]) {
        acc[key] = []
      }

      acc[key].push(item)
      return acc
    }, {})
  }

  private _getDrillLabelName(label: DrillLabelApiData): string {
    const collapsedInterceptInfo = label.edited_collapsed_intercept_info ?? label.collapsed_intercept_info ?? ''
    const expandedInterceptInfo = label.edited_expanded_intercept_info ?? label.expanded_intercept_info ?? ''
    const subInterceptInfo = label.edited_sub_intercept_info ?? label.sub_intercept_info ?? ''

    return `${label.hole_id} ${collapsedInterceptInfo} ${expandedInterceptInfo}${subInterceptInfo}`
  }

  getParentDataSet(drillLabelId: number): DrillDataSet | null {
    let parentDataSet: DrillDataSet | null = null
    this.drillDataSets.forEach(dataSet => {
      const labels = this.drillLabelManager.getLabelsForDrillDataSet(dataSet.data_set_id)
      if (labels.find(label => label.id === drillLabelId)) {
        parentDataSet = dataSet
      }
    })
    return parentDataSet
  }

  getLabelsTextSize(dataSet: DrillDataSet): number {
    const labels = this.drillLabelManager.getLabelsForDrillDataSet(dataSet.data_set_id)
    if (!labels.length) return DEFAULT_FONT_SIZE

    const container = this.drillLabelManager.getDrillLabelObject(labels[0].id)
    if (!container) return DEFAULT_FONT_SIZE

    if (container.auto_scale) return container.autoScaleSize

    const fontSize = container.textLabelContainer.style.fontSize.match(/\d+/)
    return fontSize?.length ? parseInt(fontSize[0], 10) : DEFAULT_FONT_SIZE
  }

  setLabelsTextSize(dataSetId: number, newSize: number): void {
    this.drillLabelManager.setFontSize(dataSetId, newSize)
  }

  setLabelsAutoScale(dataSetId: number, autoScale: boolean): void {
    this.drillLabelManager.setAutoScale(dataSetId, autoScale)
  }

  setDrillLabelState(id: number, state: { is_collapsed: boolean; is_image_collapsed: boolean }) {
    if (typeof state.is_collapsed === 'boolean') {
      this.drillLabelManager.toggleCollapsed(id, state.is_collapsed)
    }
  }

  toggleAllCollapsed(dataSetId: number): void {
    this.drillLabelManager.toggleAllCollapsed(dataSetId)
  }

  async updateServer(sceneId: number) {
    const mappings = await $fetch<Array<{ id: number; data_set_id: number; localization_id: number }>>(
      `/v2/drill/companies/${this.companyId}/mappers`,
      {
        ...useFetchSharedOptions(),
        headers: {
          ...useFetchSharedOptions().headers,
          'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
        },
        params: { model_id: this.modelId },
      },
    )

    const toBeAdded = this.drillDataSets
      .filter(
        ({ data_set_id, localization_id }) =>
          !mappings.find(mapping => mapping.data_set_id === data_set_id && mapping.localization_id === localization_id),
      )
      .map(dataSet => ({
        data_set_id: dataSet.data_set_id,
        localization_id: dataSet.localization_id,
        model_id: this.modelId,
      }))

    const toBeDeleted = mappings.filter(
      mapping =>
        !this.drillDataSets.find(
          ({ data_set_id, localization_id }) => data_set_id === mapping.data_set_id && localization_id === mapping.localization_id,
        ),
    )

    await Promise.all([
      toBeAdded.length
        ? $fetch(`/v2/drill/companies/${this.companyId}/mappers`, { ...useFetchSharedOptions(), method: 'POST', body: toBeAdded })
        : null,
      toBeDeleted.length
        ? $fetch(`/v2/drill/companies/${this.companyId}/mappers`, { ...useFetchSharedOptions(), method: 'DELETE', body: toBeDeleted })
        : null,

      this.drillLabelManager.updateServer(sceneId),
      this.drillHoleManager.updateDrillHoleLegends(sceneId),
    ])
  }

  getCompanyId() {
    return this.companyId
  }

  getModelId() {
    return this.modelId
  }

  mount(labelContainer: HTMLElement) {
    this.drillLabelManager.mount(labelContainer)
  }

  toggleGroupVisibility(visible: boolean) {
    this.drillLabelManager.group.visible = visible
  }

  cleanUp() {
    this.drillDataSets = []
    this.modelId = null
    this.drillLabelManager.resetData()
    this.drillHoleManager.cleanUp()
    disposeScene(this.drillLabelManager.group)
  }

  render(scene: Scene, camera: PerspectiveCamera) {
    this.drillLabelManager.render(scene, camera)
  }

  setSize(width: number, height: number) {
    this.drillLabelManager.setSize(width, height)
  }

  setMode(mode: 'view' | 'edit') {
    this.mode = mode
    this.drillLabelManager.setMode(mode)
  }
}
